/**
 * Sistema de Compra Rápida - Quick Buy Modal
 * Funcionalidades: Modal com galeria, seleção de variantes, atacado, adicionar ao carrinho
 */

// Variáveis globais
let currentProduct = null;
let currentVariants = [];
let selectedOptions = {};
let allProductImages = [];
let quickBuyBlockMode = false;

/**
 * Retorna o percentual de desconto PIX configurado (lido do botão ao abrir o modal)
 */
function getPixDiscount() {
  if (window._qbPixDiscount && !isNaN(window._qbPixDiscount)) {
    return window._qbPixDiscount;
  }
  if (window.theme && window.theme.pix && window.theme.pix.discount) {
    const val = parseFloat(window.theme.pix.discount);
    return isNaN(val) ? 5 : val;
  }
  return 5; // fallback padrão
}

/**
 * Função auxiliar para validar e converter preços
 */
function validatePrice(price) {
  if (price === null || price === undefined || price === '' || isNaN(price)) {
    return 0;
  }
  
  const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
  return isNaN(numericPrice) ? 0 : Math.max(0, numericPrice);
}

/**
 * Função auxiliar para garantir que elementos DOM existam antes de usar
 */
function safeGetElement(id) {
  const element = document.getElementById(id);
  if (!element) {
    console.warn(`Elemento não encontrado: ${id}`);
  }
  return element;
}

/**
 * Função auxiliar para atualizar texto de elemento de forma segura
 */
function safeUpdateText(elementId, text) {
  const element = safeGetElement(elementId);
  if (element) {
    element.textContent = text;
  }
}

/**
 * Abre o modal de compra rápida
 */
async function openQuickBuyModal(button) {
  const productHandle = button.dataset.productHandle;
  const productTitle = button.dataset.productTitle;
  const productPrice = button.dataset.productPrice;
  const productImage = button.dataset.productImage;
  const colorMapping = button.dataset.colorMapping || '';
  const productCollections = button.dataset.productCollections || '';
  
  // Lê desconto PIX do botão
  const pixDiscountFromBtn = button.dataset.pixDiscount;
  if (pixDiscountFromBtn && !isNaN(parseFloat(pixDiscountFromBtn))) {
    window._qbPixDiscount = parseFloat(pixDiscountFromBtn);
  } else if (window.theme && window.theme.pix && window.theme.pix.discount) {
    window._qbPixDiscount = parseFloat(window.theme.pix.discount) || 5;
  } else {
    window._qbPixDiscount = 5;
  }

  // Define o modo de exibição (block ou color)
  const colorModeBtn = button.dataset.colorMode;
  const colorModeTheme = window.theme && window.theme.colorMode;
  quickBuyBlockMode = colorModeBtn === 'block' || colorModeTheme === 'block';

  // Lê dados de inventário embutidos pelo Liquid (fonte confiável — vem do servidor)
  try {
    const invRaw = button.dataset.variantInventories;
    window._qbVariantInventories = invRaw ? JSON.parse(invRaw) : {};
  } catch(e) {
    window._qbVariantInventories = {};
    console.warn('⚠️ Erro ao parsear variant inventories:', e);
  }

  const modal = document.getElementById('quickBuyModal');
  if (!modal) return;

  // Link "Ver todos os detalhes" aponta para a PDP desde a abertura
  const detailsLink = document.getElementById('quickBuyDetailsLink');
  if (detailsLink && productHandle) detailsLink.href = '/products/' + productHandle;

  // Garante que o modal esteja direto no <body> — evita que ancestrais com
  // transform/filter quebrem o position:fixed do conteúdo
  if (modal.parentElement !== document.body) {
    document.body.appendChild(modal);
  }

  // Salva posição do scroll ANTES de fixar o body
  document.body.dataset.scrollY = window.scrollY;

  // Cancela fechamento pendente (reabrir rápido não pode ser morto pelo timeout antigo)
  if (qbCloseTimer) {
    clearTimeout(qbCloseTimer);
    qbCloseTimer = null;
  }

  // Abre o modal imediatamente — mostra skeleton enquanto carrega
  modal.classList.remove('closing');
  modal.classList.add('is-open');
  // Evita conflito com o cupom lateral
  document.getElementById('couponWidget')?.setAttribute('data-qb-open', '');

  // Trava scroll — no iOS Safari o único método confiável é position:fixed no body.
  // Como o modal já está direto no <body> (via appendChild acima) e usa
  // position:fixed com top/left/right/bottom explícitos, isso não quebra o layout.
  const isMobile = window.innerWidth <= 767;
  if (isMobile) {
    // Mobile: usa position:fixed para travar scroll no Safari iOS
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${window.scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
  } else {
    // Desktop: overflow:hidden é suficiente e não afeta o translate(-50%,-50%)
    document.body.style.overflow = 'hidden';
  }

  // Reset quantidade
  const qtyInput = document.getElementById('quickBuyQty');
  if (qtyInput) {
    qtyInput.value = '1';
    // Reseta o max para o padrão enquanto carrega
    qtyInput.max = '99';
  }

  // Preenche título e preço inicial (do botão, sem esperar fetch)
  safeUpdateText('quickBuyProductTitle', productTitle);
  safeUpdateText('quickBuySubtitle', '');
  const initialPrice = validatePrice(productPrice);
  safeUpdateText('quickBuyPrice', formatMoney(initialPrice));
  
  // Preço no botão
  const btnPriceEl = document.getElementById('quickBuyBtnPrice');
  if (btnPriceEl) btnPriceEl.textContent = formatMoney(initialPrice);
  
  // Preenche preço PIX inicial usando desconto configurado
  const pixDiscount = getPixDiscount();
  const initialPixPrice = initialPrice * (1 - pixDiscount / 100);
  safeUpdateText('quickBuyPixPrice', `${formatMoney(initialPixPrice)} no PIX (${pixDiscount}% off)`);

  // Imagem inicial enquanto carrega
  allProductImages = [{ src: productImage, alt: productTitle }];
  renderGallery(allProductImages, 0);

  // Esconde atacado/parcelas e mostra loading
  const wholesaleEl = document.getElementById('quickBuyWholesale');
  const installmentsRowEl = document.getElementById('quickBuyInstallmentsRow');
  if (wholesaleEl) wholesaleEl.style.display = 'none';
  if (installmentsRowEl) installmentsRowEl.style.display = 'none';
  showQuickBuyLoading(true);

  try {
    // Busca dados do produto (1 único request)
    const productData = await fetchProductData(productHandle);

    // Injeta color_mapping nos metafields se veio do botão
    if (!productData.metafields) productData.metafields = {};
    if (!productData.metafields.custom) productData.metafields.custom = {};

    if (colorMapping && colorMapping !== 'undefined' && colorMapping !== '') {
      productData.metafields.custom.color_mapping = { value: colorMapping };
    } else if (productCollections) {
      // Busca color_mapping das coleções em paralelo
      const collectionHandles = productCollections.split(',').map(h => h.trim()).filter(Boolean);
      const collectionResults = await Promise.allSettled(
        collectionHandles.map(h => fetchCollectionData(h))
      );
      for (const result of collectionResults) {
        if (result.status === 'fulfilled' && result.value?.metafields?.custom?.color_mapping) {
          productData.metafields.custom.color_mapping = result.value.metafields.custom.color_mapping;
          break;
        }
      }
    }

    currentProduct = productData;
    currentProduct.collectionsList = productCollections.split(',').map(c => c.trim().toLowerCase()).filter(Boolean);
    currentVariants = productData.variants;
    safeUpdateText('quickBuySubtitle', productData.type || '');

    // Atualiza galeria com todas as imagens
    if (productData.images && productData.images.length > 0) {
      allProductImages = productData.images.map(img => ({
        src: img.src || img,
        alt: productData.title
      }));
      renderGallery(allProductImages, 0);
    }

    renderWholesalePrice(productData);
    renderInstallments(productData.price);
    renderProductOptions(productData);
    renderUrgencyBadge(productData);
    
    // Atualiza limites de quantidade após carregar o produto
    updateQtyButtons();
    
    showQuickBuyLoading(false);

  } catch (error) {
    console.error('❌ Erro ao carregar produto:', error);
    showQuickBuyError('Erro ao carregar opções do produto');
  }
}

/**
 * Busca dados de uma coleção via API
 */
async function fetchCollectionData(handle) {
  try {
    const response = await fetch(`/collections/${handle}?view=json`);
    if (response.ok) {
      return await response.json();
    }
  } catch (e) {
    console.warn('Não foi possível buscar dados da coleção:', handle);
  }
  return null;
}

/**
 * Fecha o modal de compra rápida
 */
let qbCloseTimer = null;

function closeQuickBuyModal() {
  const modal = document.getElementById('quickBuyModal');
  if (!modal || modal.classList.contains('closing') || !modal.classList.contains('is-open')) return;

  modal.classList.add('closing');

  // Aguarda a animação CSS terminar (320ms) antes de resetar
  qbCloseTimer = setTimeout(() => {
    qbCloseTimer = null;
    modal.classList.remove('is-open');
    modal.classList.remove('closing');
    document.getElementById('couponWidget')?.removeAttribute('data-qb-open');
    const scrollY = parseInt(document.body.dataset.scrollY || '0');
    // Restaura o body
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    // Restaura posição do scroll (necessário quando usou position:fixed no body)
    window.scrollTo(0, scrollY);

    // Reset de estado só após fechar completamente
      currentProduct = null;
      currentVariants = [];
      selectedOptions = {};
      allProductImages = [];
      quickBuyBlockMode = false;

    const qtyInput = document.getElementById('quickBuyQty');
    if (qtyInput) qtyInput.value = 1;

    const optionsContainer = document.getElementById('quickBuyOptions');
    if (optionsContainer) optionsContainer.innerHTML = '';

    const carouselTrack = document.getElementById('qbCarouselTrack');
    if (carouselTrack) {
      carouselTrack.innerHTML = '';
      carouselTrack.style.transform = '';
    }

    const dotsContainer = document.getElementById('qbDots');
    if (dotsContainer) dotsContainer.innerHTML = '';

    qbCurrentSlide = 0;

    const wholesaleEl = document.getElementById('quickBuyWholesale');
    if (wholesaleEl) wholesaleEl.style.display = 'none';
    const installmentsRowEl = document.getElementById('quickBuyInstallmentsRow');
    if (installmentsRowEl) installmentsRowEl.style.display = 'none';
  }, 320);
}

/**
 * Estado do carrossel
 */
let qbCurrentSlide = 0;

/**
 * Renderiza o carrossel de imagens
 */
function renderGallery(images, activeIndex) {
  const track = document.getElementById('qbCarouselTrack');
  const dotsContainer = document.getElementById('qbDots');
  const prevBtn = document.getElementById('qbArrowPrev');
  const nextBtn = document.getElementById('qbArrowNext');
  
  if (!track) return;
  
  qbCurrentSlide = activeIndex || 0;
  
  // Monta slides
  track.innerHTML = '';
  images.forEach((img, index) => {
    const slide = document.createElement('div');
    slide.className = 'qb-carousel-slide';
    
    const imgEl = document.createElement('img');
    imgEl.src = img.src;
    imgEl.alt = img.alt || '';
    imgEl.decoding = 'async';
    if (index !== 0) imgEl.loading = 'lazy';
    imgEl.draggable = false;
    
    slide.appendChild(imgEl);
    track.appendChild(slide);
  });
  
  // Monta dots (só se mais de 1 imagem)
  if (dotsContainer) {
    dotsContainer.innerHTML = '';
    if (images.length > 1) {
      images.forEach((_, index) => {
        const dot = document.createElement('button');
        dot.className = 'qb-dot' + (index === qbCurrentSlide ? ' active' : '');
        dot.setAttribute('aria-label', `Imagem ${index + 1}`);
        dot.addEventListener('click', () => qbGoToSlide(index));
        dotsContainer.appendChild(dot);
      });
    }
  }
  
  // Aplica posição inicial
  qbGoToSlide(qbCurrentSlide, false);

  // Vitrine mobile: miniaturas + badges + total
  qbRenderVitrine();

  // Swipe touch
  qbInitSwipe(track);
}

/**
 * Vai para um slide específico
 */
function qbGoToSlide(index, animate) {
  const track = document.getElementById('qbCarouselTrack');
  const dotsContainer = document.getElementById('qbDots');
  const prevBtn = document.getElementById('qbArrowPrev');
  const nextBtn = document.getElementById('qbArrowNext');
  
  if (!track) return;
  
  const total = track.children.length;
  if (total === 0) return;
  
  // Clamp
  qbCurrentSlide = Math.max(0, Math.min(index, total - 1));
  
  // Move track
  if (animate === false) {
    track.style.transition = 'none';
  } else {
    track.style.transition = 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)';
  }
  track.style.transform = `translateX(-${qbCurrentSlide * 100}%)`;
  
  // Atualiza dots
  if (dotsContainer) {
    dotsContainer.querySelectorAll('.qb-dot').forEach((dot, i) => {
      dot.classList.toggle('active', i === qbCurrentSlide);
    });
  }
  
  // Atualiza setas
  if (prevBtn) prevBtn.disabled = qbCurrentSlide === 0;
  if (nextBtn) nextBtn.disabled = qbCurrentSlide === total - 1;

  // Vitrine mobile: sincroniza miniatura ativa
  qbSyncThumbs();
}

/**
 * Avança ou recua no carrossel
 */
function qbSlide(direction) {
  const track = document.getElementById('qbCarouselTrack');
  if (!track) return;
  const total = track.children.length;
  qbGoToSlide(qbCurrentSlide + direction);
}

/**
 * Inicializa swipe touch no carrossel
 */
function qbInitSwipe(track) {
  let startX = 0;
  let isDragging = false;
  
  track.addEventListener('touchstart', (e) => {
    startX = e.touches[0].clientX;
    isDragging = true;
  }, { passive: true });
  
  track.addEventListener('touchend', (e) => {
    if (!isDragging) return;
    isDragging = false;
    const diff = startX - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) {
      qbSlide(diff > 0 ? 1 : -1);
    }
  }, { passive: true });
  
  // Mouse drag (desktop)
  let mouseStartX = 0;
  let isMouseDragging = false;
  
  track.addEventListener('mousedown', (e) => {
    mouseStartX = e.clientX;
    isMouseDragging = true;
    track.style.cursor = 'grabbing';
  });
  
  track.addEventListener('mouseup', (e) => {
    if (!isMouseDragging) return;
    isMouseDragging = false;
    track.style.cursor = '';
    const diff = mouseStartX - e.clientX;
    if (Math.abs(diff) > 40) {
      qbSlide(diff > 0 ? 1 : -1);
    }
  });
  
  track.addEventListener('mouseleave', () => {
    isMouseDragging = false;
    track.style.cursor = '';
  });
}

/**
 * Renderiza badge de urgência (últimas unidades)
 */
function renderUrgencyBadge(product) {
  const urgencyEl = document.getElementById('quickBuyUrgency');
  const urgencyTextEl = document.getElementById('quickBuyUrgencyText');
  if (!urgencyEl || !urgencyTextEl) return;

  // Usa dados de inventário do Liquid (mais confiável)
  const inventories = window._qbVariantInventories || {};
  const availableVariants = product.variants.filter(v => v.available);

  // Calcula estoque total usando dados do Liquid
  let totalInventory = 0;
  let hasInventoryData = false;
  product.variants.forEach(v => {
    const inv = inventories[v.id];
    if (inv && inv.m === 'shopify' && inv.p !== 'continue') {
      hasInventoryData = true;
      const qty = parseInt(inv.q) || 0;
      if (qty > 0) totalInventory += qty;
    }
  });

  if (hasInventoryData && availableVariants.length > 0 && totalInventory > 0 && totalInventory <= 10) {
    urgencyTextEl.textContent = `Últimas ${totalInventory}!`;
    urgencyEl.style.display = 'flex';
  } else if (availableVariants.length > 0 && availableVariants.length <= 2) {
    urgencyTextEl.textContent = `Últimas unidades!`;
    urgencyEl.style.display = 'flex';
  } else {
    urgencyEl.style.display = 'none';
  }
}

/**
 * Renderiza preço de atacado
 */
function renderWholesalePrice(product) {
  const wholesaleEl = document.getElementById('quickBuyWholesale');
  const wholesalePriceEl = document.getElementById('quickBuyWholesalePrice');
  
  if (!wholesaleEl || !wholesalePriceEl) return;
  
  // Tenta obter o preço de atacado dos metafields
  let wholesalePrice = null;
  
  if (product.metafields) {
    const ws = product.metafields.custom?.wholesale_price?.value ||
               product.metafields.custom?.wholesale_price ||
               product.metafields.wholesale_price?.value ||
               product.metafields.wholesale_price;
    
    wholesalePrice = validatePrice(ws);
  }
  
  if (wholesalePrice > 0) {
    // Converte para centavos se necessário
    const priceCents = wholesalePrice < 1000 ? wholesalePrice * 100 : wholesalePrice;
    const validatedPriceCents = validatePrice(priceCents);
    
    if (validatedPriceCents > 0) {
      wholesalePriceEl.textContent = formatMoney(validatedPriceCents);
      wholesaleEl.style.display = 'flex';
    } else {
      wholesaleEl.style.display = 'none';
    }
  } else {
    wholesaleEl.style.display = 'none';
  }
}

/**
 * Renderiza parcelas (12x sem juros)
 */
function renderInstallments(priceCents) {
  const el = document.getElementById('quickBuyInstallments');
  const rowEl = document.getElementById('quickBuyInstallmentsRow');
  if (!el) return;
  
  const validatedPrice = validatePrice(priceCents);
  
  if (validatedPrice <= 0) {
    if (rowEl) rowEl.style.display = 'none';
    return;
  }
  
  let installments = 12;
  let interestRate = 1;
  let displayMode = 'info_sem_juros';
  let showInstallments = true;
  
  if (window.theme && window.theme.installments) {
    const inst = window.theme.installments;
    showInstallments = inst.show;
    
    // Verifica se o produto está em coleção promocional
    let isPromo = false;
    if (inst.categoryEnabled && inst.promoCollections && currentProduct && currentProduct.collectionsList) {
      const promoCollectionsList = inst.promoCollections.split(',').map(c => c.trim().toLowerCase()).filter(Boolean);
      isPromo = currentProduct.collectionsList.some(c => promoCollectionsList.includes(c));
    }
    
    if (isPromo) {
      installments = parseInt(inst.promoMaxInstallments) || 3;
      interestRate = parseFloat(inst.promoInterestRate) || 1;
      displayMode = inst.promoDisplayMode || 'info_sem_juros';
    } else {
      installments = parseInt(inst.maxInstallments) || 12;
      interestRate = parseFloat(inst.interestRate) || 1;
      displayMode = inst.displayMode || 'info_sem_juros';
    }
  }
  
  if (!showInstallments) {
    if (rowEl) rowEl.style.display = 'none';
    return;
  }
  
  let installmentValue;
  if (displayMode === 'info_sem_juros') {
    installmentValue = validatedPrice / installments;
    el.innerHTML = `até <b>${installments}x</b> de <b>${formatMoney(installmentValue)}</b> <span style="color: var(--product-on-sale-accent, #7c3aed); font-size: 10px; font-weight: 600;">SEM JUROS</span>`;
  } else if (displayMode === 'info_juros') {
    installmentValue = (validatedPrice * interestRate) / installments;
    el.innerHTML = `<b>${installments}x</b> de <b>${formatMoney(installmentValue)}</b> <span style="color: #b1b5c3; font-size: 10px; font-weight: 600;">COM JUROS</span>`;
  } else {
    installmentValue = (validatedPrice * interestRate) / installments;
    el.innerHTML = `até <b>${installments}x</b> de <b>${formatMoney(installmentValue)}</b>`;
  }
  
  // Assegura que o span de parcelas esteja visível
  el.style.display = '';
  if (rowEl) rowEl.style.display = 'flex';
}

/**
 * Busca dados completos do produto via API
 * Usa apenas /products/handle.js para evitar double request
 */
async function fetchProductData(handle) {
  const response = await fetch(`/products/${handle}.js`);
  if (!response.ok) {
    throw new Error('Produto não encontrado');
  }
  return await response.json();
}


function renderProductOptions(product) {
  const optionsContainer = document.getElementById('quickBuyOptions');
  optionsContainer.innerHTML = '';
  
  if (!product.options || product.options.length === 0) {
    return;
  }
  
  // Labels de cor para detectar opções de cor
  const colorLabels = ['color', 'colour', 'couleur', 'cor', 'colore', 'farbe', 'modelo', 'escolha sua cor'];
  
  // Labels de ícone para detectar opções que devem usar ícones
  const iconLabels = ['tamanho', 'size', 'taille', 'taglia', 'größe', 'material', 'sabor', 'flavor', 'tipo', 'type', 'estilo', 'style'];
  
  product.options.forEach((option, optionIndex) => {
    if (option.name.toLowerCase() === 'title') return; // Pula a opção padrão "Title"
    
    const optionDiv = document.createElement('div');
    optionDiv.className = 'quick-buy-option';
    
    const label = document.createElement('label');
    label.textContent = option.name + ':';
    label.dataset.optionLabel = option.name;
    optionDiv.appendChild(label);
    
    const valuesDiv = document.createElement('div');
    valuesDiv.className = 'quick-buy-option-values';
    
    // Verifica se é uma opção de cor
    const isColorOption = colorLabels.some(colorLabel => 
      option.name.toLowerCase().includes(colorLabel)
    );
    
    // Verifica se é uma opção que deve usar ícones
    const isIconOption = iconLabels.some(iconLabel => 
      option.name.toLowerCase().includes(iconLabel)
    );
    
    option.values.forEach(value => {
      const valueButton = document.createElement('button');
      valueButton.type = 'button';
      valueButton.className = 'option-value';
      valueButton.dataset.option = option.name;
      valueButton.dataset.value = value;
      valueButton.title = value;
      
      // Encontra a variante correspondente a este valor (prefere a que combina com o já selecionado)
      let variant = null;
      if (selectedOptions && currentProduct) {
        variant = product.variants.find(v => {
          if (v[`option${optionIndex + 1}`] !== value) return false;
          return currentProduct.options.every((opt, idx) => {
            if (idx === optionIndex) return true;
            if (opt.name.toLowerCase() === 'title') return true;
            const sel = selectedOptions[opt.name];
            return !sel || v[`option${idx + 1}`] === sel;
          });
        });
      }
      if (!variant) variant = product.variants.find(v => v[`option${optionIndex + 1}`] === value);

      // Guarda a imagem da variante para navegar na galeria ao selecionar (todos os modos, inclusive bloco)
      if (variant && variant.featured_image && variant.featured_image.src) {
        valueButton.dataset.variantImage = variant.featured_image.src;
      }

      // Block mode: renderiza todas as opções como botões de texto
      if (quickBuyBlockMode) {
        valueButton.classList.add('block-swatch-option');
        valueButton.textContent = value;
      }
      // Se for opção de cor, adiciona classe especial e aplica estilo de bolinha colorida
      else if (isColorOption) {
        valueButton.classList.add('color-swatch-option');
        
        // Se a variante tem imagem, guarda para navegar na galeria ao selecionar
        if (variant && variant.featured_image && variant.featured_image.src) {
          valueButton.dataset.variantImage = variant.featured_image.src;
        }
        
        // Obtém dados de cor (cor CSS ou imagem)
        const colorData = getColorDataForValue(product, option.name, value, optionIndex);
        
        if (colorData.image) {
          valueButton.style.cssText = `
            background-image: url(${colorData.image}) !important;
            background-size: cover !important;
            background-position: center !important;
            background-color: #f0f0f0 !important;
          `;
        } else if (colorData.color) {
          valueButton.style.cssText = `
            background-color: ${colorData.color} !important;
            background-image: none !important;
          `;
        } else {
          const cssColor = value.toLowerCase().replace(/\s+/g, '');
          valueButton.style.cssText = `
            background-color: ${cssColor} !important;
            background-image: none !important;
          `;
        }
        
        // Cor clara: adiciona borda visível
        if (isWhiteColor(value) || (colorData.color && isLightColorHex(colorData.color))) {
          valueButton.classList.add('color-swatch--white');
        }
        
        valueButton.setAttribute('aria-label', value);
        valueButton.innerHTML = ''; // Sem texto — é bolinha
      } 
      // Se for opção de ícone, adiciona classe especial e renderiza ícone
      else if (isIconOption) {
        valueButton.classList.add('icon-option');
        
        // Adiciona classe especial para tamanhos (formato circular)
        const sizeLabels = ['tamanho', 'size', 'taille', 'taglia', 'größe'];
        const isSizeOption = sizeLabels.some(sizeLabel => 
          option.name.toLowerCase().includes(sizeLabel)
        );
        if (isSizeOption) {
          valueButton.classList.add('size-option');
        }
        
        // Obtém dados de ícone
        const iconData = getIconDataForValue(product, option.name, value, optionIndex);
        
        // Renderiza o ícone baseado no tipo
        let iconHTML = '';
        switch (iconData.iconType) {
          case 'svg':
            iconHTML = iconData.icon;
            break;
          case 'emoji':
            iconHTML = `<span class="icon-emoji">${iconData.icon}</span>`;
            break;
          case 'class':
            iconHTML = `<i class="${iconData.icon}"></i>`;
            break;
          case 'text':
          default:
            iconHTML = `<span class="icon-text">${iconData.icon}</span>`;
            break;
        }
        
        // Para tamanhos, mostra apenas o ícone (sem label)
        if (isSizeOption) {
          valueButton.innerHTML = `
            <div class="icon-option-content">
              <div class="icon-option-icon">${iconHTML}</div>
            </div>
          `;
        } else {
          valueButton.innerHTML = `
            <div class="icon-option-content">
              <div class="icon-option-icon">${iconHTML}</div>
              <div class="icon-option-label">${value}</div>
            </div>
          `;
        }
        
        valueButton.setAttribute('aria-label', value);
      } 
      // Para opções que não são cores nem ícones, mantém o texto
      else {
        valueButton.textContent = value;
      }
      
      // Verifica se esta opção está disponível
      const isAvailable = checkOptionAvailability(option.name, value, product.variants);
      if (!isAvailable) {
        valueButton.classList.add('unavailable');
        // Não desabilita — permite clicar mesmo esgotado
      }
      
      valueButton.addEventListener('click', () => selectOption(option.name, value, valueButton));
      valuesDiv.appendChild(valueButton);
    });
    
    optionDiv.appendChild(valuesDiv);
    optionsContainer.appendChild(optionDiv);
  });
  
  // Seleciona primeira opção disponível de cada tipo
  autoSelectFirstAvailableOptions(product);
}

/**
 * Tenta obter dados de cor (cor ou imagem) de um valor específico
 */
function getColorDataForValue(product, optionName, value, optionIndex) {
  const result = { color: null, image: null };
  
  console.log('🎨 Buscando cor para:', value);
  console.log('📦 Produto metafields:', product.metafields);
  
  // 1. PRIORIDADE: Tenta obter do metafield color_mapping do produto
  if (product.metafields) {
    const mappingValue = product.metafields.custom?.color_mapping?.value || 
                        product.metafields.custom?.color_mapping ||
                        product.metafields.color_mapping?.value ||
                        product.metafields.color_mapping ||
                        product.metafields.color?.mapping;
    
    console.log('🗺️ Color mapping encontrado:', mappingValue);
    
    if (mappingValue && mappingValue !== 'undefined') {
      const mappings = mappingValue.split('|');
      const valueNormalized = value.trim().toLowerCase();
      
      for (const map of mappings) {
        const parts = map.split(':');
        if (parts.length >= 2) {
          const name = parts[0].trim();
          const nameNormalized = name.toLowerCase();
          const colorValue = parts[1].trim();
          
          console.log(`  Comparando "${name}" com "${value}"`);
          
          if (name === value || nameNormalized === valueNormalized) {
            result.color = colorValue;
            console.log('✅ Encontrou cor no mapping (PRIORIDADE):', result.color);
            return result;
          }
        }
      }
    }
  }
  
  // 2. Tenta obter cor do metafield da variante
  const variant = product.variants.find(v => v[`option${optionIndex + 1}`] === value);
  if (variant && variant.metafields) {
    console.log('🔍 Variante metafields:', variant.metafields);
    if (variant.metafields.custom && variant.metafields.custom.color) {
      result.color = variant.metafields.custom.color.value || variant.metafields.custom.color;
      console.log('✅ Encontrou cor da variante (custom):', result.color);
      return result;
    }
    if (variant.metafields.color) {
      result.color = variant.metafields.color.value || variant.metafields.color.color;
      console.log('✅ Encontrou cor da variante (color):', result.color);
      return result;
    }
  }
  
  // 3. Tenta obter das tags do produto
  if (product.tags) {
    console.log('🏷️ Tags do produto:', product.tags);
    for (const tag of product.tags) {
      if (tag.includes('color:')) {
        const tagParts = tag.replace('color:', '').split(':');
        if (tagParts.length >= 2 && tagParts[0].trim() === value) {
          result.color = tagParts[1].trim();
          console.log('✅ Encontrou cor nas tags:', result.color);
          return result;
        }
      }
    }
  }
  
  // 4. ÚLTIMA OPÇÃO: Tenta obter imagem da variante (só se não tiver cor)
  if (variant && variant.featured_image && variant.featured_image.src) {
    result.image = variant.featured_image.src.replace(/\.(jpg|jpeg|png|gif|webp)/, '_64x64.$1');
    console.log('⚠️ Usando imagem da variante como fallback:', result.image);
    return result;
  }
  
  // 5. FALLBACK FINAL: Usa mapeamento de cores em português ou nome da cor como CSS
  console.log('⚠️ Usando fallback - mapeamento de cores PT-BR para:', value);
  
  // Mapeamento de cores comuns em português
  const colorMapPT = {
    'preto': '#000000',
    'branco': '#FFFFFF',
    'vermelho': '#FF0000',
    'azul': '#0000FF',
    'verde': '#008000',
    'amarelo': '#FFFF00',
    'rosa': '#FFC0CB',
    'roxo': '#800080',
    'laranja': '#FFA500',
    'marrom': '#A52A2A',
    'cinza': '#808080',
    'bege': '#F5F5DC',
    'nude': '#E3C4A8',
    'cru': '#F5F5DC',
    'areia': '#C2B280',
    'caramelo': '#D2691E',
    'vinho': '#722F37',
    'mostarda': '#FFDB58',
    'coral': '#FF7F50',
    'turquesa': '#40E0D0'
  };
  
  const valueNormalized = value.toLowerCase().trim();
  if (colorMapPT[valueNormalized]) {
    result.color = colorMapPT[valueNormalized];
    console.log('✅ Encontrou cor no mapeamento PT-BR:', result.color);
  } else {
    // Fallback final: nome da cor como CSS
    const cssColor = valueNormalized.replace(/\s+/g, '');
    result.color = cssColor;
    console.log('⚠️ Usando nome da cor como CSS:', cssColor);
  }
  
  return result;
}

/**
 * Tenta obter dados de ícone de um valor específico
 */
function getIconDataForValue(product, optionName, value, optionIndex) {
  const result = { icon: null, iconType: 'text' }; // iconType pode ser 'svg', 'class', 'emoji', 'text'
  
  console.log('🎯 Buscando ícone para:', value);
  
  // 1. PRIORIDADE: Tenta obter do metafield icon_mapping do produto
  if (product.metafields) {
    const mappingValue = product.metafields.custom?.icon_mapping?.value || 
                        product.metafields.custom?.icon_mapping ||
                        product.metafields.icon_mapping?.value ||
                        product.metafields.icon_mapping ||
                        product.metafields.icon?.mapping;
    
    console.log('🗺️ Icon mapping encontrado:', mappingValue);
    
    if (mappingValue && mappingValue !== 'undefined') {
      const mappings = mappingValue.split('|');
      const valueNormalized = value.trim().toLowerCase();
      
      for (const map of mappings) {
        const parts = map.split(':');
        if (parts.length >= 2) {
          const name = parts[0].trim();
          const nameNormalized = name.toLowerCase();
          const iconValue = parts[1].trim();
          
          console.log(`  Comparando "${name}" com "${value}"`);
          
          if (name === value || nameNormalized === valueNormalized) {
            // Detecta o tipo de ícone
            if (iconValue.startsWith('<svg') || iconValue.includes('</svg>')) {
              result.icon = iconValue;
              result.iconType = 'svg';
            } else if (iconValue.match(/^[\u{1F000}-\u{1F9FF}]$/u) || iconValue.match(/^[\u{2600}-\u{26FF}]$/u)) {
              result.icon = iconValue;
              result.iconType = 'emoji';
            } else if (iconValue.startsWith('fa-') || iconValue.includes('icon-') || iconValue.includes('material-')) {
              result.icon = iconValue;
              result.iconType = 'class';
            } else {
              result.icon = iconValue;
              result.iconType = 'text';
            }
            console.log('✅ Encontrou ícone no mapping (PRIORIDADE):', result);
            return result;
          }
        }
      }
    }
  }
  
  // 2. Tenta obter ícone do metafield da variante
  const variant = product.variants.find(v => v[`option${optionIndex + 1}`] === value);
  if (variant && variant.metafields) {
    console.log('🔍 Variante metafields para ícone:', variant.metafields);
    if (variant.metafields.custom && variant.metafields.custom.icon) {
      const iconValue = variant.metafields.custom.icon.value || variant.metafields.custom.icon;
      result.icon = iconValue;
      result.iconType = detectIconType(iconValue);
      console.log('✅ Encontrou ícone da variante (custom):', result);
      return result;
    }
    if (variant.metafields.icon) {
      const iconValue = variant.metafields.icon.value || variant.metafields.icon.icon;
      result.icon = iconValue;
      result.iconType = detectIconType(iconValue);
      console.log('✅ Encontrou ícone da variante (icon):', result);
      return result;
    }
  }
  
  // 3. Tenta obter das tags do produto
  if (product.tags) {
    console.log('🏷️ Tags do produto para ícone:', product.tags);
    for (const tag of product.tags) {
      if (tag.includes('icon:')) {
        const tagParts = tag.replace('icon:', '').split(':');
        if (tagParts.length >= 2 && tagParts[0].trim() === value) {
          const iconValue = tagParts[1].trim();
          result.icon = iconValue;
          result.iconType = detectIconType(iconValue);
          console.log('✅ Encontrou ícone nas tags:', result);
          return result;
        }
      }
    }
  }
  
  // 4. FALLBACK: Mapeamento de ícones comuns baseado no nome
  const iconMapPT = {
    // Tamanhos
    'pp': '👶', 'p': 'P', 'm': 'M', 'g': 'G', 'gg': 'GG', 'xg': 'XG', 'xxg': 'XXG',
    'xs': 'XS', 's': 'S', 'l': 'L', 'xl': 'XL', 'xxl': 'XXL',
    'pequeno': '📏', 'medio': '📐', 'grande': '📊',
    
    // Materiais
    'algodao': '🌱', 'algodão': '🌱', 'cotton': '🌱',
    'poliester': '🧵', 'poliéster': '🧵', 'polyester': '🧵',
    'seda': '✨', 'silk': '✨',
    'linho': '🌾', 'linen': '🌾',
    'couro': '🐄', 'leather': '🐄',
    
    // Cores básicas (emojis)
    'preto': '⚫', 'branco': '⚪', 'vermelho': '🔴', 'azul': '🔵', 
    'verde': '🟢', 'amarelo': '🟡', 'rosa': '🌸', 'roxo': '🟣',
    'laranja': '🟠', 'marrom': '🤎', 'cinza': '⚫',
    
    // Sabores
    'chocolate': '🍫', 'baunilha': '🍦', 'morango': '🍓',
    'limao': '🍋', 'limão': '🍋', 'laranja': '🍊',
    
    // Outros
    'novo': '✨', 'classico': '👑', 'clássico': '👑',
    'premium': '💎', 'basico': '⭐', 'básico': '⭐'
  };
  
  const valueNormalized = value.toLowerCase().trim();
  if (iconMapPT[valueNormalized]) {
    result.icon = iconMapPT[valueNormalized];
    result.iconType = 'emoji';
    console.log('✅ Encontrou ícone no mapeamento PT-BR:', result);
  } else {
    // Fallback final: primeira letra em maiúscula
    result.icon = value.charAt(0).toUpperCase();
    result.iconType = 'text';
    console.log('⚠️ Usando primeira letra como ícone:', result);
  }
  
  return result;
}

/**
 * Detecta o tipo de ícone baseado no conteúdo
 */
function detectIconType(iconValue) {
  if (!iconValue) return 'text';
  
  if (iconValue.startsWith('<svg') || iconValue.includes('</svg>')) {
    return 'svg';
  } else if (iconValue.match(/^[\u{1F000}-\u{1F9FF}]$/u) || iconValue.match(/^[\u{2600}-\u{26FF}]$/u)) {
    return 'emoji';
  } else if (iconValue.startsWith('fa-') || iconValue.includes('icon-') || iconValue.includes('material-')) {
    return 'class';
  } else {
    return 'text';
  }
}

/**
 * Verifica se o nome da cor é branco
 */
function isWhiteColor(colorName) {
  const whitenames = ['white', 'blanc', 'branco', 'bianco', 'weiß', 'blanco'];
  return whitenames.some(name => colorName.toLowerCase().includes(name));
}

/**
 * Verifica se uma cor hex é clara
 */
function isLightColorHex(hex) {
  if (!hex || !hex.startsWith('#')) return false;
  
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.8;
}



/**
 * Verifica se uma opção está disponível
 */
function checkOptionAvailability(optionName, optionValue, variants) {
  return variants.some(variant => {
    const optionIndex = currentProduct.options.findIndex(opt => opt.name === optionName);
    if (optionIndex === -1) return false;
    
    const variantOption = variant[`option${optionIndex + 1}`];
    return variantOption === optionValue && variant.available;
  });
}

/**
 * Seleciona automaticamente as primeiras opções disponíveis
 */
function autoSelectFirstAvailableOptions(product) {
  product.options.forEach(option => {
    if (option.name.toLowerCase() === 'title') return;
    
    const firstAvailable = option.values.find(value => 
      checkOptionAvailability(option.name, value, product.variants)
    );
    
    if (firstAvailable) {
      const button = document.querySelector(`[data-option="${option.name}"][data-value="${firstAvailable}"]`);
      if (button && !button.disabled) {
        selectOption(option.name, firstAvailable, button);
      }
    }
  });
}

/**
 * Seleciona uma opção
 */
function selectOption(optionName, optionValue, button) {
  // Permite clicar mesmo em opções esgotadas (mostra "Esgotado" no botão de compra)
  
  // Remove seleção anterior da mesma opção
  document.querySelectorAll(`[data-option="${optionName}"]`).forEach(btn => {
    btn.classList.remove('selected');
  });
  
  // Adiciona seleção atual
  button.classList.add('selected');
  selectedOptions[optionName] = optionValue;

  // Atualiza o label da opção com o valor selecionado
  const optionLabel = document.querySelector(`[data-option-label="${optionName}"]`);
  if (optionLabel) {
    optionLabel.textContent = optionName + ': ' + optionValue;
  }
  
  // Atualiza disponibilidade de outras opções
  updateOptionAvailability();
  
  // Atualiza variante selecionada
  updateSelectedVariant();

  // Atualiza a imagem pela variante exata selecionada (funciona em todos os modos, ex. bloco)
  if (currentProduct && currentVariants) {
    const exactVariant = currentVariants.find(variant => {
      return currentProduct.options.every((option, index) => {
        if (option.name.toLowerCase() === 'title') return true;
        const variantOption = variant[`option${index + 1}`];
        const selectedOption = selectedOptions[option.name];
        return !selectedOption || variantOption === selectedOption;
      });
    });
    if (exactVariant && exactVariant.featured_image && exactVariant.featured_image.src) {
      updateProductImage(exactVariant.featured_image.src);
      return;
    }
  }

  // Fallback: se for uma opção de cor e tiver imagem guardada no botão, atualiza a imagem principal
  const colorLabels = ['color', 'colour', 'couleur', 'cor', 'colore', 'farbe', 'modelo', 'escolha sua cor'];
  const isColorOption = colorLabels.some(label => optionName.toLowerCase().includes(label));
  
  if (isColorOption && button.dataset.variantImage) {
    updateProductImage(button.dataset.variantImage);
  }
}

/**
 * Atualiza a imagem do produto (navega para o slide correspondente)
 */
function updateProductImage(imageUrl) {
  const track = document.getElementById('qbCarouselTrack');
  if (!track) return;
  
  // Procura o slide que corresponde à URL
  const slides = track.querySelectorAll('.qb-carousel-slide img');
  let targetIndex = -1;
  
  const normalizeUrl = (url) => url.split('?')[0].replace(/_\d+x\d+(\.\w+)$/, '$1');
  const targetFile = normalizeUrl(imageUrl).split('/').pop();
  
  slides.forEach((img, index) => {
    if (normalizeUrl(img.src).split('/').pop() === targetFile) {
      targetIndex = index;
    }
  });
  
  if (targetIndex >= 0) {
    qbGoToSlide(targetIndex);
  }
}

/**
 * Atualiza disponibilidade das opções baseado na seleção atual
 */
function updateOptionAvailability() {
  if (!currentProduct || !currentProduct.options) return;
  
  currentProduct.options.forEach(option => {
    if (option.name.toLowerCase() === 'title') return;
    
    option.values.forEach(value => {
      const button = document.querySelector(`[data-option="${option.name}"][data-value="${value}"]`);
      if (!button) return;
      
      // Cria uma cópia das opções selecionadas com esta opção
      const testOptions = { ...selectedOptions, [option.name]: value };
      
      // Verifica se existe variante disponível com essas opções
      const isAvailable = currentVariants.some(variant => {
        if (!variant.available) return false;
        
        return currentProduct.options.every((opt, index) => {
          if (opt.name.toLowerCase() === 'title') return true;
          const variantOption = variant[`option${index + 1}`];
          const selectedOption = testOptions[opt.name];
          return !selectedOption || variantOption === selectedOption;
        });
      });
      
      button.classList.toggle('unavailable', !isAvailable);
      // Não desabilita — permite selecionar mesmo esgotado
    });
  });
}

/**
 * Atualiza a variante selecionada e preço
 */
function updateSelectedVariant() {
  if (!currentProduct || !currentVariants) return;
  
  // Encontra a variante que corresponde às opções selecionadas
  const selectedVariant = currentVariants.find(variant => {
    return currentProduct.options.every((option, index) => {
      if (option.name.toLowerCase() === 'title') return true;
      const variantOption = variant[`option${index + 1}`];
      const selectedOption = selectedOptions[option.name];
      return !selectedOption || variantOption === selectedOption;
    });
  });
  
  if (selectedVariant) {
    // Atualiza ID da variante
    const variantIdEl = safeGetElement('quickBuyVariantId');
    if (variantIdEl) {
      variantIdEl.value = selectedVariant.id;
    }
    
    // Atualiza preço
    const validatedPrice = validatePrice(selectedVariant.price);
    safeUpdateText('quickBuyPrice', formatMoney(validatedPrice));
    
    // Atualiza preço no botão
    const btnPriceEl = document.getElementById('quickBuyBtnPrice');
    if (btnPriceEl) btnPriceEl.textContent = formatMoney(validatedPrice);
    
    // Atualiza preço PIX usando desconto configurado
    const pixDiscount = getPixDiscount();
    const pixPrice = validatedPrice * (1 - pixDiscount / 100);
    safeUpdateText('quickBuyPixPrice', `${formatMoney(pixPrice)} no PIX (${pixDiscount}% off)`);
    
    // Atualiza preço comparativo se houver
    const comparePriceEl = safeGetElement('quickBuyComparePrice');
    if (comparePriceEl) {
      const comparePrice = validatePrice(selectedVariant.compare_at_price);
      if (comparePrice > 0 && comparePrice > validatedPrice) {
        comparePriceEl.textContent = formatMoney(comparePrice);
        comparePriceEl.style.display = 'inline';
      } else {
        comparePriceEl.style.display = 'none';
      }
    }
    
    // Atualiza parcelas
    renderInstallments(selectedVariant.price);
    
    // Atualiza botão de adicionar
    const addBtn = safeGetElement('quickBuyAddBtn');
    if (addBtn) {
      const btnPriceSpan = document.getElementById('quickBuyBtnPrice');
      if (selectedVariant.available) {
        addBtn.disabled = false;
        addBtn.style.background = '#6b7280';
        if (btnPriceSpan) btnPriceSpan.textContent = formatMoney(validatedPrice);
      } else {
        addBtn.disabled = true;
        addBtn.style.background = '#d1d5db';
        if (btnPriceSpan) btnPriceSpan.textContent = 'Indisponível';
      }
    }
    
    // Atualiza botão comprar agora
    const buyNowBtn = safeGetElement('quickBuyBuyNowBtn');
    if (buyNowBtn) {
      buyNowBtn.disabled = !selectedVariant.available;
    }

    // Atualiza limites de quantidade conforme estoque da variante
    updateQtyButtons();

    // Vitrine mobile: total = variante x qtd
    qbUpdateTotal();
  }
}

/**
 * Retorna o estoque máximo disponível para a variante selecionada.
 * Usa dados de inventário embutidos pelo Liquid no botão (fonte confiável).
 */
function getSelectedVariantMaxQty() {
  if (!currentProduct || !currentVariants) return 99;

  // Encontra a variante selecionada
  const selectedVariant = currentVariants.find(variant => {
    return currentProduct.options.every((option, index) => {
      if (option.name.toLowerCase() === 'title') return true;
      const variantOption = variant[`option${index + 1}`];
      const selectedOption = selectedOptions[option.name];
      return !selectedOption || variantOption === selectedOption;
    });
  });

  if (!selectedVariant) return 99;

  // Usa os dados de inventário embutidos pelo Liquid (m=management, p=policy, q=quantity)
  const inventories = window._qbVariantInventories || {};
  const inv = inventories[selectedVariant.id];

  if (!inv) return 99;

  // Se não gerencia estoque ou política é "continue", sem limite
  if (inv.m !== 'shopify' || inv.p === 'continue') {
    return 99;
  }

  const qty = parseInt(inv.q);
  if (isNaN(qty) || qty <= 0) return 0;
  return qty;
}

/**
 * Atualiza o estado dos botões +/- e o atributo max do input de quantidade
 */
function updateQtyButtons() {
  const qtyInput = document.getElementById('quickBuyQty');
  if (!qtyInput) return;

  const maxQty = getSelectedVariantMaxQty();

  console.log(`📦 Estoque máximo: ${maxQty}`);

  // Se esgotado (maxQty === 0), mantém qty em 1 visualmente mas desativa botões
  if (maxQty === 0) {
    qtyInput.value = 1;
    qtyInput.max = 1;
    const btnMinus = qtyInput.previousElementSibling;
    const btnPlus = qtyInput.nextElementSibling;
    if (btnMinus) btnMinus.disabled = true;
    if (btnPlus) btnPlus.disabled = true;
    return;
  }

  // Sempre garante mínimo 1 ao trocar de variante
  const currentQty = Math.max(1, parseInt(qtyInput.value) || 1);
  qtyInput.value = currentQty;
  qtyInput.max = maxQty;

  // Garante que a quantidade não ultrapasse o máximo
  if (currentQty > maxQty) {
    qtyInput.value = maxQty;
  }

  // Botão de diminuir
  const btnMinus = qtyInput.previousElementSibling;
  if (btnMinus) btnMinus.disabled = parseInt(qtyInput.value) <= 1;

  // Botão de aumentar
  const btnPlus = qtyInput.nextElementSibling;
  if (btnPlus) btnPlus.disabled = maxQty !== 99 && parseInt(qtyInput.value) >= maxQty;
}

/**
 * Altera quantidade respeitando o estoque disponível
 */
function changeQuantity(delta) {
  const qtyInput = document.getElementById('quickBuyQty');
  if (!qtyInput) return;

  const maxQty = getSelectedVariantMaxQty();
  const currentQty = parseInt(qtyInput.value) || 1;
  const newQty = Math.max(1, Math.min(maxQty, currentQty + delta));
  qtyInput.value = newQty;

  updateQtyButtons();
  qbUpdateTotal();
}

/**
 * Compra agora (adiciona ao carrinho e redireciona para checkout)
 */
async function buyNowQuick() {
  const variantId = document.getElementById('quickBuyVariantId').value;
  const quantity = parseInt(document.getElementById('quickBuyQty').value) || 1;
  
  if (!variantId) {
    alert('Por favor, selecione todas as opções do produto.');
    return;
  }
  
  const buyBtn = document.getElementById('quickBuyBuyNowBtn');
  if (buyBtn) {
    buyBtn.disabled = true;
    buyBtn.textContent = 'Aguarde...';
  }
  
  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ id: variantId, quantity: quantity })
    });
    
    if (response.ok) {
      window.location.href = '/checkout';
    } else {
      const err = await response.json();
      throw new Error(err.message || 'Erro ao processar compra');
    }
  } catch (error) {
    alert(error.message || 'Erro ao processar. Tente novamente.');
    if (buyBtn) {
      buyBtn.disabled = false;
      buyBtn.textContent = 'Comprar agora';
    }
  }
}

/**
 * Adiciona produto ao carrinho
 */
async function addToCartQuick() {
  const variantIdEl = safeGetElement('quickBuyVariantId');
  const qtyEl = safeGetElement('quickBuyQty');
  const addBtn = safeGetElement('quickBuyAddBtn');
  const btnPriceEl = document.getElementById('quickBuyBtnPrice');

  const variantId = variantIdEl ? parseInt(variantIdEl.value) : 0;
  const quantity = qtyEl ? (parseInt(qtyEl.value) || 1) : 1;

  console.log('🛒 addToCartQuick chamado | variantId:', variantId, '| qty:', quantity);

  if (!variantId || isNaN(variantId) || variantId <= 0) {
    alert('Selecione todas as opções do produto antes de adicionar.');
    return;
  }

  if (quantity <= 0 || isNaN(quantity)) {
    alert('Quantidade inválida.');
    return;
  }

  // Valida quantidade contra estoque disponível
  const maxQty = getSelectedVariantMaxQty();
  if (quantity > maxQty && maxQty !== 99) {
    alert(`Quantidade máxima disponível: ${maxQty} unidades.`);
    if (qtyEl) qtyEl.value = maxQty;
    return;
  }

  // Estado de loading
  if (addBtn) addBtn.disabled = true;
  if (btnPriceEl) btnPriceEl.textContent = 'Adicionando...';

  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: quantity })
    });

    const json = await res.json();

    if (!res.ok) {
      throw new Error(json.description || json.message || 'Erro ao adicionar');
    }

    console.log('✅ Produto adicionado:', json);

    // Feedback verde
    const btnPriceSpan = document.getElementById('quickBuyBtnPrice');
    if (btnPriceSpan) btnPriceSpan.textContent = '✓ Adicionado!';
    if (addBtn) addBtn.style.background = 'linear-gradient(135deg,#10b981,#059669)';

    // Dispara eventos do tema
    document.documentElement.dispatchEvent(new CustomEvent('product:added', { 
      bubbles: true, 
      detail: { 
        scrollToTop: false,
        variant: json,
        quantity: quantity
      } 
    }));

    // Fecha modal; no mobile não abre o mini-cart automaticamente
    setTimeout(() => {
      closeQuickBuyModal();
      if (window.innerWidth <= 767) {
        // Rede de segurança: se qualquer gatilho abrir o mini-cart após o add, fecha
        var mcGuard = safeGetElement('mini-cart');
        var ovGuard = safeGetElement('mini-cart-overlay');
        var forceCloseMiniCart = function() {
          if (mcGuard && mcGuard.getAttribute('aria-hidden') === 'false') {
            mcGuard.setAttribute('aria-hidden', 'true');
            document.body.classList.remove('no-mobile-scroll');
            if (ovGuard) ovGuard.classList.remove('is-active', 'is-visible');
            document.body.style.overflow = '';
          }
        };
        forceCloseMiniCart();
        if (mcGuard && typeof MutationObserver !== 'undefined') {
          var mcObs = new MutationObserver(forceCloseMiniCart);
          mcObs.observe(mcGuard, { attributes: true, attributeFilter: ['aria-hidden'] });
          setTimeout(function() { mcObs.disconnect(); }, 2000);
        }
        return;
      }
      const miniCart = safeGetElement('mini-cart');
      if (miniCart) {
        miniCart.setAttribute('aria-hidden', 'false');
        const overlay = safeGetElement('mini-cart-overlay');
        if (overlay) overlay.setAttribute('aria-hidden', 'false');
      }
    }, 700);

  } catch (err) {
    console.error('❌ Erro ao adicionar:', err);
    alert(err.message || 'Erro ao adicionar ao carrinho. Tente novamente.');
    if (addBtn) {
      addBtn.disabled = false;
      addBtn.style.background = '';
    }
    if (btnPriceEl) {
      // Restaura o total (preço x qtd) no botão
      if (typeof qbUpdateTotal === 'function') qbUpdateTotal();
      else {
        const currentPrice = document.getElementById('quickBuyPrice');
        btnPriceEl.textContent = currentPrice ? currentPrice.textContent : 'Adicionar';
      }
    }
  }
}

/**
 * Atualiza o contador do carrinho
 */
async function updateCartCount() {
  try {
    // Busca o estado atual do carrinho
    const response = await fetch('/cart.js');
    const cart = await response.json();
    
    console.log('🛒 Carrinho atualizado:', cart);
    console.log('📊 Total de itens:', cart.item_count);
    
    // Lista de todos os possíveis seletores de contador de carrinho
    const selectors = [
      '[data-cart-count]',
      '.cart-count',
      '.cart__count',
      '[data-header-cart-count]',
      '.header__cart-count',
      '.cart-link__bubble',
      '.cart__item-count',
      '#cart-count',
      '.js-cart-count',
      '[data-cart-item-count]',
      '.header-cart-count',
      '.site-header__cart-count',
      '.cart-count-bubble'
    ];
    
    // Atualiza todos os elementos encontrados
    let elementsUpdated = 0;
    selectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(el => {
        el.textContent = cart.item_count;
        el.innerText = cart.item_count;
        el.setAttribute('data-cart-count', cart.item_count);
        
        // Mostra o badge se tiver itens
        if (cart.item_count > 0) {
          el.style.display = '';
          el.style.visibility = 'visible';
          el.style.opacity = '1';
          el.classList.remove('hidden');
          
          // Animação de destaque
          el.style.animation = 'none';
          setTimeout(() => {
            el.style.animation = 'cartBounce 0.6s ease';
          }, 10);
        }
        
        elementsUpdated++;
        console.log(`✅ Atualizado: ${selector} = ${cart.item_count}`);
      });
    });
    
    console.log(`📈 Total de elementos atualizados: ${elementsUpdated}`);
    
    // Atualiza via atributo data no body/html
    document.body.setAttribute('data-cart-count', cart.item_count);
    document.documentElement.setAttribute('data-cart-count', cart.item_count);
    
    // Dispara eventos para o tema
    const cartRefreshEvent = new CustomEvent('cart:refresh', {
      bubbles: true,
      detail: { 
        cart: cart,
        item_count: cart.item_count,
        scrollToTop: false 
      }
    });
    document.documentElement.dispatchEvent(cartRefreshEvent);
    
    const cartUpdatedEvent = new CustomEvent('cart:updated', {
      bubbles: true,
      detail: { 
        cart: cart,
        item_count: cart.item_count
      }
    });
    document.dispatchEvent(cartUpdatedEvent);
    
    // Evento adicional para compatibilidade
    window.dispatchEvent(new CustomEvent('cart:change', {
      detail: { cart: cart }
    }));
    
    // Força atualização do tema se existir
    if (typeof window.theme !== 'undefined' && window.theme.cartCount !== undefined) {
      window.theme.cartCount = cart.item_count;
    }
    
    console.log('✅ Contador do carrinho atualizado para:', cart.item_count);
    
    // Força um refresh visual após 100ms
    setTimeout(() => {
      selectors.forEach(selector => {
        const elements = document.querySelectorAll(selector);
        elements.forEach(el => {
          el.textContent = cart.item_count;
          if (cart.item_count > 0) {
            el.style.display = '';
          }
        });
      });
    }, 100);
    
    return cart;
    
  } catch (error) {
    console.error('❌ Erro ao atualizar contador do carrinho:', error);
    return null;
  }
}

/**
 * Vai para página completa do produto
 */
function viewFullProduct() {
  if (currentProduct) {
    window.location.href = `/products/${currentProduct.handle}`;
  }
}

/**
 * Mostra/esconde loading
 */
function showQuickBuyLoading(show) {
  const loading = document.getElementById('quickBuyLoading');
  const options = document.getElementById('quickBuyOptions');
  
  if (loading) loading.style.display = show ? 'flex' : 'none';
  if (options) options.style.display = show ? 'none' : 'flex';
}

/**
 * Mostra erro
 */
function showQuickBuyError(message) {
  const loading = document.getElementById('quickBuyLoading');
  if (!loading) return;
  loading.style.display = 'flex';
  loading.innerHTML = `
    <div style="text-align: center; color: #ef4444; display:flex; flex-direction:column; align-items:center; gap:12px;">
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="15" y1="9" x2="9" y2="15"></line>
        <line x1="9" y1="9" x2="15" y2="15"></line>
      </svg>
      <p style="margin:0;">${message}</p>
      <button onclick="closeQuickBuyModal()" style="padding: 8px 16px; background: #ef4444; color: white; border: none; border-radius: 6px; cursor: pointer;">Fechar</button>
    </div>
  `;
}

/**
 * Formata preço
 */
function formatMoney(cents) {
  // Usa a função de validação
  const validatedCents = validatePrice(cents);
  
  // Tenta usar a função do tema se disponível
  if (typeof Currency !== 'undefined' && Currency.formatMoney && window.theme && window.theme.moneyFormat) {
    return Currency.formatMoney(validatedCents, window.theme.moneyFormat);
  }
  
  // Fallback para nossa implementação
  const value = validatedCents / 100;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
}

/**
 * Fecha modal com ESC
 */
document.addEventListener('keydown', function(e) {
  if (e.key === 'Escape') {
    const modal = document.getElementById('quickBuyModal');
    if (modal && modal.classList.contains('is-open')) {
      closeQuickBuyModal();
    }
  }
});

/**
 * Previne scroll do body quando modal está aberto
 */
document.addEventListener('DOMContentLoaded', function() {
  const modal = document.getElementById('quickBuyModal');
  if (modal) {
    modal.addEventListener('wheel', function(e) {
      e.stopPropagation();
    });
  }

  // Validação de digitação manual no input de quantidade
  const qtyInput = document.getElementById('quickBuyQty');
  if (qtyInput) {
    qtyInput.addEventListener('change', function() {
      const maxQty = getSelectedVariantMaxQty();
      let val = parseInt(this.value) || 1;
      val = Math.max(1, Math.min(maxQty, val));
      this.value = val;
      updateQtyButtons();
      qbUpdateTotal();
    });
    qtyInput.addEventListener('input', function() {
      // Bloqueia digitação de valores inválidos em tempo real
      const maxQty = getSelectedVariantMaxQty();
      let val = parseInt(this.value);
      if (!isNaN(val) && val > maxQty) {
        this.value = maxQty;
      }
      updateQtyButtons();
      qbUpdateTotal();
    });
  }

  // Event listeners para fechar modal e ver produto
  document.addEventListener('click', function(e) {
    if (e.target.dataset.action === 'close-modal' || e.target.closest('[data-action="close-modal"]')) {
      e.preventDefault();
      e.stopPropagation();
      closeQuickBuyModal();
      return false;
    }
    if (e.target.dataset.action === 'view-full-product' || e.target.closest('[data-action="view-full-product"]')) {
      e.preventDefault();
      e.stopPropagation();
      viewFullProduct();
      return false;
    }
  });
});

/**
 * Função auxiliar para debug
 */
function debugQuickBuy() {
  console.log('Quick Buy Debug Info:');
  console.log('Current Product:', currentProduct);
  console.log('Current Variants:', currentVariants);
  console.log('Selected Options:', selectedOptions);
  console.log('Modal Element:', document.getElementById('quickBuyModal'));
}

/**
 * Função de teste para formatação de dinheiro
 */
function testFormatMoney() {
  console.log('Testando formatação de dinheiro:');
  console.log('formatMoney(23990):', formatMoney(23990)); // Deve retornar R$ 239,90
  console.log('formatMoney(null):', formatMoney(null)); // Deve retornar R$ 0,00
  console.log('formatMoney(undefined):', formatMoney(undefined)); // Deve retornar R$ 0,00
  console.log('formatMoney("abc"):', formatMoney("abc")); // Deve retornar R$ 0,00
  console.log('formatMoney(NaN):', formatMoney(NaN)); // Deve retornar R$ 0,00
  console.log('formatMoney(""):', formatMoney("")); // Deve retornar R$ 0,00
}

// Executa teste automaticamente no console para debug
if (typeof window !== 'undefined' && window.console) {
  window.testFormatMoney = testFormatMoney;
  window.debugQuickBuy = debugQuickBuy;
}


/**
 * Função auxiliar para forçar atualização do contador
 */
function forceUpdateCartBadge(itemCount) {
  console.log('🔄 Forçando atualização do badge para:', itemCount);
  
  // Atualiza especificamente o badge do tema
  const headerCartCount = document.querySelector('.header__cart-count');
  if (headerCartCount) {
    headerCartCount.textContent = itemCount;
    headerCartCount.innerText = itemCount;
    headerCartCount.style.display = itemCount > 0 ? '' : 'none';
    console.log('✅ Badge .header__cart-count atualizado');
  }
  
  // Atualiza todos os outros possíveis badges
  const allBadges = document.querySelectorAll('[data-cart-count], .cart-count, .cart__count');
  allBadges.forEach(badge => {
    badge.textContent = itemCount;
    badge.innerText = itemCount;
  });
  
  // Força repaint do navegador
  document.body.style.display = 'none';
  document.body.offsetHeight; // Trigger reflow
  document.body.style.display = '';
}

/**
 * Lógica de touch drag para fechar modal no mobile
 */
document.addEventListener('DOMContentLoaded', function() {
  const modal = document.getElementById('quickBuyModal');
  if (!modal) return;
  
  const content = modal.querySelector('.quick-buy-content');
  if (!content) return;

  let startY = 0;
  let currentY = 0;
  let isDragging = false;
  const DRAG_THRESHOLD = 100; // Pixels to swipe before closing
  
  // Aplica o touchstart na barra superior (handle) ou em toda a tela (se desejar).
  // Vamos aplicar no modal inteiro, mas só arrastar se vier de cima ou se scrollbar estiver no topo
  const dragHandle = modal.querySelector('.quick-buy-drag-handle');
  
  if (dragHandle) {
    dragHandle.addEventListener('touchstart', handleTouchStart, {passive: true});
    dragHandle.addEventListener('touchmove', handleTouchMove, {passive: false});
    dragHandle.addEventListener('touchend', handleTouchEnd);
  }

  function handleTouchStart(e) {
    if (window.innerWidth >= 768) return; // Only on mobile
    startY = e.touches[0].clientY;
    isDragging = true;
    content.style.transition = 'none'; // Disable transition for smooth dragging
  }

  function handleTouchMove(e) {
    if (!isDragging) return;
    currentY = e.touches[0].clientY;
    const diff = currentY - startY;
    
    // Only allow dragging down
    if (diff > 0) {
      e.preventDefault();
      content.style.transform = `translateY(${diff}px)`;
    }
  }

  function handleTouchEnd(e) {
    if (!isDragging) return;
    isDragging = false;
    
    content.style.transition = 'transform 0.4s cubic-bezier(0.32, 0.72, 0, 1)'; // Restore transition
    
    const diff = currentY - startY;
    
    if (diff > DRAG_THRESHOLD) {
      // Swiped enough to close
      closeQuickBuyModal();
    } else {
      // Snap back
      content.style.transform = 'translateY(0)';
    }
    
    // Reset values after transition
    setTimeout(() => {
      if (modal.classList.contains('is-open') && !modal.classList.contains('closing')) {
        content.style.transform = '';
      }
    }, 400);
  }
});

/**
 * ===== VITRINE MOBILE (port NegaRosa) =====
 * Miniaturas, badges New/Discount, total (variante x qtd) e compartilhar.
 */
function qbSelectedVariant() {
  if (!currentProduct || !currentVariants) return null;
  return currentVariants.find(function (variant) {
    return currentProduct.options.every(function (option, index) {
      if (option.name.toLowerCase() === 'title') return true;
      var variantOption = variant['option' + (index + 1)];
      var selectedOption = selectedOptions[option.name];
      return !selectedOption || variantOption === selectedOption;
    });
  }) || null;
}

function qbUpdateTotal() {
  var totalEl = document.getElementById('quickBuyTotal');
  var btnEl = document.getElementById('quickBuyBtnPrice');
  var addBtn = document.getElementById('quickBuyAddBtn');
  var v = qbSelectedVariant();
  var qtyEl = document.getElementById('quickBuyQty');
  var qty = qtyEl ? (parseInt(qtyEl.value, 10) || 1) : 1;
  if (!v) {
    if (totalEl) totalEl.textContent = '—';
    return;
  }
  var total = validatePrice(v.price) * qty;
  var txt = formatMoney(total);
  if (totalEl) totalEl.textContent = txt;
  // Total visível somente no botão
  if (!v.available) {
    if (btnEl) btnEl.textContent = 'Indisponível';
    return;
  }
  if (btnEl && addBtn && addBtn.disabled && btnEl.textContent !== txt) {
    // Não sobrescreve estado de loading ("Adicionando...")
    var loading = (btnEl.textContent || '').toLowerCase();
    if (loading.indexOf('adicionando') !== -1 || loading.indexOf('adicionado') !== -1 || loading.indexOf('✓') !== -1) return;
  }
  if (btnEl) btnEl.textContent = txt;
}

function qbRenderVitrine() {
  // Miniaturas (até 4)
  var box = document.getElementById('qbThumbs');
  if (box) {
    box.innerHTML = '';
    allProductImages.slice(0, 4).forEach(function (img, i) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'qb-vt-thumb' + (i === qbCurrentSlide ? ' active' : '');
      b.setAttribute('aria-label', 'Ver foto ' + (i + 1));
      b.style.backgroundImage = 'url("' + img.src + '")';
      b.addEventListener('click', function (e) { e.stopPropagation(); qbGoToSlide(i); });
      box.appendChild(b);
    });
  }
  // Badges New / Discount
  var badges = document.getElementById('qbBadges');
  if (badges) {
    var html = '';
    var tags = '';
    if (currentProduct && currentProduct.tags) {
      tags = Array.isArray(currentProduct.tags) ? currentProduct.tags.join(' ') : String(currentProduct.tags);
    }
    if (/(^|[\s,])(novo|new|lançamento|lancamento)([\s,]|$)/i.test(tags)) {
      html += '<span class="qb-vt-badge qb-vt-badge--new">New</span>';
    }
    var avail = (currentVariants || []).filter(function (v) { return v.available; });
    var v0 = avail[0] || currentVariants[0];
    if (v0) {
      var p = validatePrice(v0.price);
      var c = validatePrice(v0.compare_at_price);
      if (c > p) html += '<span class="qb-vt-badge qb-vt-badge--off">Discount ' + Math.round((c - p) / c * 100) + '%</span>';
    }
    badges.innerHTML = html;
  }
  qbUpdateTotal();
}

function qbSyncThumbs() {
  var box = document.getElementById('qbThumbs');
  if (!box) return;
  box.querySelectorAll('.qb-vt-thumb').forEach(function (t, i) {
    t.classList.toggle('active', i === qbCurrentSlide);
  });
}

function qbShare() {
  var handle = currentProduct ? currentProduct.handle : '';
  var url = window.location.origin + '/products/' + handle;
  if (navigator.share) {
    navigator.share({ title: (currentProduct && currentProduct.title) || document.title, url: url }).catch(function () {});
    return;
  }
  if (navigator.clipboard) {
    navigator.clipboard.writeText(url).then(function () {
      var btn = document.getElementById('qbShareBtn');
      if (!btn || btn.dataset.done) return;
      btn.dataset.done = '1';
      var orig = btn.innerHTML;
      btn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>';
      setTimeout(function () { btn.innerHTML = orig; delete btn.dataset.done; }, 1500);
    }, function () {});
  }
}

/**
 * Mobile: tocar no card abre o drawer em vez de navegar para a PDP.
 * Com variantes usa o botão de compra rápida; variante única usa os
 * data attributes do card (fallback sem navegação).
 */
document.addEventListener('click', function (e) {
  if (window.innerWidth > 767) return;
  var link = e.target.closest('.product-item__link-wrapper');
  if (!link) return;
  if (e.target.closest('button, a:not(.product-item__link-wrapper)')) return;
  var card = link.closest('.product-item');
  if (!card) return;
  if (card.querySelector('button[disabled]')) return;
  var qbBtn = card.querySelector('button[data-product-handle]');
  if (qbBtn && qbBtn.getAttribute('onclick')) {
    e.preventDefault();
    e.stopPropagation();
    openQuickBuyModal(qbBtn);
    return;
  }
  var handle = card.getAttribute('data-product-handle');
  if (!handle) return;
  e.preventDefault();
  e.stopPropagation();
  openQuickBuyModal({ dataset: {
    productHandle: handle,
    productTitle: card.getAttribute('data-product-title') || '',
    productPrice: card.getAttribute('data-product-price') || '',
    productImage: card.getAttribute('data-product-image') || '',
    colorMapping: '',
    productCollections: '',
    pixDiscount: '',
    colorMode: '',
    variantInventories: '{}'
  } });
});
